/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useTheme } from '../theme/ThemeContext';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ThemedButton from '../components/common/ThemedButton';
import { inlineButton } from '../components/athlohive/EventFormFields';
import {
  EventActivitiesStep, EventBasicsStep, EventRegistrationStep,
  EventReviewStep, EventScheduleStep, EventSponsorsStep,
} from '../components/athlohive/EventCreateSteps';
import { buildEventBatch } from '../components/athlohive/buildEventBatch';
import { buildEventReadBatch, hydrateEventForm } from '../components/athlohive/eventEditPayloads';
import { describeBatchError } from '../components/athlohive/describeBatchError';
import { eventFormReducer, initialEventForm } from '../components/athlohive/eventFormReducer';
import { readLookup } from '../components/athlohive/lookupOptions';
import { validateEventForm } from '../components/athlohive/validateEventForm';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/**
 * Creates an AthloHive event and everything that hangs off it in one atomic
 * write.
 *
 * The event, its activities, each activity's route or venue, the pit stops, the
 * registration window and the sponsors are separate tables. Saving them one at
 * a time leaves a half-built event behind whenever an admin is interrupted, so
 * the whole wizard submits as a single batch: every row lands, or none does.
 */

/** Which form fields belong to which step, so a failed check can jump back. */
const STEPS = [
  { key: 'basics', label: 'Basics', fields: ['title', 'description', 'prerequisites', 'preparation_steps', 'cover_image_url', 'creator_type', 'creator_organization_id', 'organizer'] },
  { key: 'schedule', label: 'Schedule', fields: ['location', 'location_state', 'location_country', 'location_pincode', 'timezone', 'start_datetime', 'end_datetime', 'activity_mode'] },
  { key: 'activities', label: 'Activities', fields: ['activities', 'activityErrors'] },
  { key: 'registration', label: 'Registration', fields: ['registration_source', 'registration_type_id', 'registration_start_datetime', 'registration_end_datetime', 'max_participants', 'external_url'] },
  { key: 'sponsors', label: 'Sponsors', fields: [] },
  { key: 'review', label: 'Review', fields: [] },
];

/** Tables whose rows fill the wizard's pickers. */
const LOOKUP_TABLES = {
  organizations: 'organization',
  activityTypes: 'activity_type',
  registrationTypes: 'registration_type',
  distanceUnits: 'distance_measuring_unit',
};

const UNSAVED_CHANGES_MESSAGE =
  'Your event has unsaved changes. If you leave or refresh this page, your '
  + 'entered information will be lost. Are you sure you want to continue?';

export default function EventCreatePage() {
  const { id: applicationId, eventId } = useParams();
  const isEdit = !!eventId;
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { showAlertDialog } = useAlertDialog();

  const [form, dispatch] = useReducer(eventFormReducer, undefined, initialEventForm);
  const [stepIndex, setStepIndex] = useState(0);
  const [errors, setErrors] = useState({});
  // Which steps the admin has actually tried to leave. Errors are shown only
  // for those: marking a step they have never visited reads as "you did
  // something wrong" when they simply have not got there yet.
  const [attemptedSteps, setAttemptedSteps] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const [dirty, setDirty] = useState(false);

  // The application names its organization; every write is scoped by both.
  const { data: application, isLoading: loadingApplication } = useQuery({
    queryKey: ['application', applicationId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/details/${applicationId}/`, { method: 'GET', signal }
      );
      if (!resp.ok) throw new Error('Could not load this application.');
      return resp.json();
    },
    enabled: !!applicationId,
    staleTime: 60_000,
  });

  // ApplicationListSerializer nests the organization as an object; older
  // payloads exposed a bare id. Accept either rather than depend on which
  // serializer answered.
  const organizationId = application?.organization?.id
    ?? application?.organization_id
    ?? (typeof application?.organization === 'number' ? application.organization : undefined);

  const lookupQueries = useQuery({
    queryKey: ['athlohiveLookups', applicationId, organizationId],
    queryFn: async ({ signal }) => {
      const entries = await Promise.all(
        Object.entries(LOOKUP_TABLES).map(async ([key, tableName]) => {
          const resp = await apiHelper.apiCall('/arcee/application/table/fetch/', {
            method: 'POST',
            signal,
            body: JSON.stringify({
              table_name: tableName,
              organization_id: organizationId,
              application_id: Number(applicationId),
              page_size: 200,
            }),
          });
          if (!resp.ok) return [key, { rows: [], labelKey: null }];
          return [key, readLookup(await resp.json())];
        })
      );
      return Object.fromEntries(entries);
    },
    enabled: !!organizationId && !!applicationId,
    staleTime: 60_000,
  });

  // Editing loads the event and every child in a single read, the mirror of the
  // write. Children are matched to their parent by @ref, so one round trip
  // rebuilds the whole form.
  const existing = useQuery({
    queryKey: ['athlohiveEvent', applicationId, organizationId, eventId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall('/arcee/application/table/read/records/', {
        method: 'POST',
        signal,
        body: JSON.stringify(buildEventReadBatch(eventId, {
          organizationId, applicationId: Number(applicationId),
        })),
      });
      if (!resp.ok) throw new Error('Could not load this event.');
      return resp.json();
    },
    enabled: isEdit && !!organizationId && !!applicationId,
    staleTime: 0,
  });

  useEffect(() => {
    if (!isEdit || !existing.data) return;
    const hydrated = hydrateEventForm(existing.data.results || existing.data);
    if (hydrated) dispatch({ type: 'hydrate', value: hydrated });
  }, [isEdit, existing.data]);

  const emptyLookup = { rows: [], labelKey: null };
  const lookups = useMemo(() => ({
    organizations: emptyLookup, activityTypes: emptyLookup,
    registrationTypes: emptyLookup, distanceUnits: emptyLookup,
    ...(lookupQueries.data || {}),
  }), [lookupQueries.data]);

  // Re-validate as the admin types, but only surface errors once they have
  // tried to move on — inline red text on an untouched form is just noise.
  useEffect(() => {
    setErrors(validateEventForm(form));
  }, [form]);

  // A half-filled wizard is worth a confirm — on refresh and close the browser
  // owns the prompt, and in-app links are caught before they navigate.
  const confirmLeave = useUnsavedChangesGuard(dirty, UNSAVED_CHANGES_MESSAGE);

  // The page is clean until the admin actually changes something. Hydrating an
  // existing event dispatches directly, so opening the Edit screen and reading
  // it does not count as a change; everything the admin does goes through here.
  const guardedDispatch = (action) => {
    setDirty(true);
    dispatch(action);
  };

  /** Navigate away from the wizard, asking first if there is work to lose. */
  const leaveTo = (path) => {
    if (!confirmLeave()) return;
    navigate(path);
  };

  const batch = useMemo(() => {
    if (!organizationId) return null;
    try {
      return buildEventBatch(form, {
        organizationId, applicationId: Number(applicationId),
      });
    } catch {
      return null;
    }
  }, [form, organizationId, applicationId]);

  const save = useMutation({
    mutationFn: async (status) => {
      const payload = { ...form, status };
      const body = buildEventBatch(payload, {
        organizationId, applicationId: Number(applicationId),
      });
      const resp = await apiHelper.apiCall('/arcee/application/table/save/records/', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok) throw result;
      return result;
    },
    onSuccess: (result) => {
      setDirty(false);
      showAlertDialog({
        title: isEdit ? 'Event updated' : 'Event created',
        message: `The event and its ${result?.operations?.length ?? 0} related record groups were saved together.`,
        type: 'success',
        okLabel: 'Done',
        onOk: () => navigate(`/dashboard/applications/${applicationId}`),
      });
    },
    onError: (error) => setSubmitError(describeBatchError(error)),
  });

  const stepErrors = (index) => {
    const step = STEPS[index];
    return step.fields.filter((field) => errors[field]);
  };

  const markAttempted = (index) => setAttemptedSteps(
    (current) => (current.includes(index) ? current : [...current, index])
  );

  const goNext = () => {
    markAttempted(stepIndex);
    if (stepErrors(stepIndex).length) return;
    setStepIndex((current) => Math.min(current + 1, STEPS.length - 1));
  };

  const goBack = () => setStepIndex((current) => Math.max(current - 1, 0));

  const submit = (status) => {
    const allErrors = validateEventForm(form);
    if (Object.keys(allErrors).length) {
      // Submitting is a claim that the whole form is done, so every step is
      // fair game to flag now.
      setAttemptedSteps(STEPS.map((_, index) => index));
      const firstBroken = STEPS.findIndex((step) => step.fields.some((field) => allErrors[field]));
      if (firstBroken >= 0) setStepIndex(firstBroken);
      return;
    }
    setSubmitError(null);
    save.mutate(status);
  };

  const visibleErrors = attemptedSteps.includes(stepIndex) ? errors : {};
  const stepProps = {
    form, errors: visibleErrors, dispatch: guardedDispatch, lookups, isEdit,
    organizationId, applicationId,
  };

  if (loadingApplication || (isEdit && existing.isLoading)) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }

  if (isEdit && existing.isError) {
    return (
      <div style={{ padding: 24, color: '#d53b3b' }}>
        This event could not be loaded. It may have been deleted.
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <div style={{ marginBottom: 6, fontSize: 13, color: '#6b7280' }}>
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => leaveTo(`/dashboard/applications/${applicationId}`)}
        >
          {application?.name || 'Application'}
        </span>
        {isEdit ? ' / Edit event' : ' / Create event'}
      </div>
      <h2 style={{ color: theme.primary, marginBottom: 18 }}>
        {isEdit ? `Edit event${form.title ? `: ${form.title}` : ''}` : 'Create event'}
      </h2>

      {/* Step rail */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {STEPS.map((step, index) => {
          const active = index === stepIndex;
          const broken = attemptedSteps.includes(index) && stepErrors(index).length > 0;
          return (
            <button
              key={step.key}
              type="button"
              onClick={() => setStepIndex(index)}
              style={{
                border: `1.4px solid ${broken ? '#d53b3b' : (active ? theme.primary : '#e5e7eb')}`,
                background: active ? theme.primary : '#fff',
                color: active ? '#fff' : (broken ? '#d53b3b' : theme.text),
                borderRadius: 999,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {index + 1}. {step.label}
            </button>
          );
        })}
      </div>

      {lookupQueries.isError && (
        <div style={{ color: '#d53b3b', marginBottom: 12 }}>
          Some pickers could not be loaded. Saving is still blocked until they are chosen.
        </div>
      )}

      {STEPS[stepIndex].key === 'basics' && (
        <EventBasicsStep
          {...stepProps}
          onAddOrganization={() => leaveTo(
            `/dashboard/applications/${applicationId}/athlohive/organizations/create`
          )}
        />
      )}
      {STEPS[stepIndex].key === 'schedule' && <EventScheduleStep {...stepProps} />}
      {STEPS[stepIndex].key === 'activities' && <EventActivitiesStep {...stepProps} />}
      {STEPS[stepIndex].key === 'registration' && <EventRegistrationStep {...stepProps} />}
      {STEPS[stepIndex].key === 'sponsors' && <EventSponsorsStep {...stepProps} />}
      {STEPS[stepIndex].key === 'review' && (
        <EventReviewStep form={form} operationCount={batch?.operations?.length ?? 0} />
      )}

      {submitError && (
        <div
          style={{
            border: '1px solid #d53b3b', background: '#fdf2f2', color: '#991b1b',
            borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 14,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Nothing was saved.</div>
          <div>{submitError.message}</div>
          {submitError.table && (
            <div style={{ marginTop: 6, fontSize: 13 }}>
              Failed while writing <strong>{submitError.table}</strong>
              {submitError.index !== undefined && ` (operation ${submitError.index + 1})`}.
              The whole batch was rolled back, so you can correct it and save again.
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', marginTop: 8 }}>
        <ThemedButton type="button" variant="outlined" style={inlineButton} onClick={goBack} disabled={stepIndex === 0}>
          Back
        </ThemedButton>

        <div style={{ display: 'flex', gap: 12 }}>
          {stepIndex < STEPS.length - 1 && (
            <ThemedButton type="button" style={inlineButton} onClick={goNext}>Next</ThemedButton>
          )}
          {stepIndex === STEPS.length - 1 && (
            <>
              <ThemedButton
                type="button" variant="outlined" style={inlineButton}
                disabled={save.isPending}
                onClick={() => submit('DRAFT')}
              >
                {save.isPending ? 'Saving…' : 'Save as draft'}
              </ThemedButton>
              <ThemedButton
                type="button" style={inlineButton}
                disabled={save.isPending}
                onClick={() => submit('PUBLISHED')}
              >
                {save.isPending ? 'Saving…' : (isEdit ? 'Save changes' : 'Publish')}
              </ThemedButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
